import { LogoMark } from "@shared/components/Logo";
import { cn } from "@ui/lib";
import {
	ArrowUpIcon,
	BatteryFullIcon,
	CheckIcon,
	ChefHatIcon,
	GripIcon,
	MicIcon,
	MicOffIcon,
	PhoneOffIcon,
	PlusIcon,
	SearchIcon,
	ShoppingBagIcon,
	SignalHighIcon,
	Volume2Icon,
	WifiIcon,
} from "lucide-react";
import type { HeroProduct } from "../site";
import { DEMO_STORE } from "../site";
import { HERO_SCENES, type HeroFrame, type HeroScene } from "./timeline";

/**
 * The hero phone's screen, drawn from one frame of the timeline: the store's chat, or a call
 * with it. Plain HTML, so it is crisp at any size and readable without JavaScript.
 */
export function PhoneScreen({ frame }: { frame: HeroFrame }) {
	const scene = HERO_SCENES[frame.scene] ?? HERO_SCENES[0];
	if (!scene) {
		return null;
	}
	return (
		<div className="flex h-full flex-col bg-(--mk-white) text-(--mk-ink)">
			<StatusBar />
			{scene.kind === "call" ? (
				<CallView frame={frame} products={scene.products} />
			) : (
				<ChatView frame={frame} scene={scene} />
			)}
		</div>
	);
}

type ChatScene = Extract<HeroScene, { kind: "chat" }>;

/** The store's chat: the shopper's message, the search step, the reply and its products. */
function ChatView({ frame, scene }: { frame: HeroFrame; scene: ChatScene }) {
	return (
		<>
			<ChatHeader basket={frame.basket} />
			<div className="mk-noscroll flex flex-1 flex-col justify-end gap-2.5 overflow-hidden px-3.5 pb-3">
				<p className="text-center text-(--mk-faint) text-[11px]">
					{scene.label}
				</p>
				{frame.sent ? <ShopperBubble text={scene.user} /> : null}
				{frame.step ? (
					<SearchStep
						label={scene.step}
						done={Boolean(frame.reply)}
					/>
				) : null}
				{frame.reply ? <ReplyBubble text={frame.reply} /> : null}
				{scene.recipe ? (
					<RecipeCard
						frame={frame}
						products={scene.products}
						recipe={scene.recipe}
					/>
				) : (
					<ProductRow frame={frame} products={scene.products} />
				)}
			</div>
			<BasketBar frame={frame} />
			<Composer typed={frame.typed} />
		</>
	);
}

function ShopperBubble({ text }: { text: string }) {
	return (
		<div
			dir="auto"
			className="mk-pop ms-auto max-w-[84%] rounded-[18px] rounded-br-md bg-(--mk-ink) px-3.5 py-2 text-(--mk-white) text-[13px] leading-snug"
		>
			{text}
		</div>
	);
}

function ReplyBubble({ text }: { text: string }) {
	return (
		<div
			dir="auto"
			className="max-w-[90%] rounded-[18px] rounded-bl-md bg-(--mk-mist) px-3.5 py-2 text-[13px] leading-snug"
		>
			{text}
		</div>
	);
}

/** The search the assistant ran: a magnifier while it runs, a check once it answered. */
function SearchStep({ label, done }: { label: string; done: boolean }) {
	const Icon = done ? CheckIcon : SearchIcon;
	return (
		<div className="mk-pop flex items-center gap-1.5 text-(--mk-muted) text-[11px]">
			<span className="grid size-4 place-items-center rounded-full bg-(--mk-mist)">
				<Icon className="size-2.5" aria-hidden />
			</span>
			{label}
		</div>
	);
}

/** The message box: what the shopper is typing, and the send button once there is text. */
function Composer({ typed }: { typed: string }) {
	const Icon = typed ? ArrowUpIcon : MicIcon;
	return (
		<div className="flex items-center gap-2 px-3.5 pb-5">
			<div className="flex min-h-10 flex-1 items-center rounded-full bg-(--mk-mist) px-3.5 text-[13px]">
				{typed ? (
					<span dir="auto" className="mk-caret truncate">
						{typed}
					</span>
				) : (
					<span className="text-(--mk-faint)">
						Ask {DEMO_STORE.name}…
					</span>
				)}
			</div>
			<span
				className={cn(
					"grid size-10 shrink-0 place-items-center rounded-full transition-colors",
					typed
						? "bg-(--mk-ink) text-(--mk-white)"
						: "bg-(--mk-mist) text-(--mk-ink)",
				)}
			>
				<Icon className="size-4" aria-hidden />
			</span>
		</div>
	);
}

function StatusBar() {
	return (
		<div className="flex items-center justify-between px-6 pt-3.5 pb-1 font-semibold text-[11px]">
			<span>9:41</span>
			<span className="flex items-center gap-1">
				<SignalHighIcon className="size-3" aria-hidden />
				<WifiIcon className="size-3" aria-hidden />
				<BatteryFullIcon className="size-3.5" aria-hidden />
			</span>
		</div>
	);
}

function ChatHeader({ basket }: { basket: number }) {
	return (
		<header className="flex items-center gap-2.5 border-(--mk-line) border-b px-4 pt-2 pb-3">
			<LogoMark className="size-8" />
			<div className="min-w-0 flex-1">
				<p className="font-semibold text-[13px] leading-tight">
					{DEMO_STORE.name}
				</p>
				<p className="flex items-center gap-1 text-(--mk-muted) text-[11px]">
					<span className="size-1.5 rounded-full bg-(--mk-signal)" />
					Shopping assistant
				</p>
			</div>
			<BasketIcon basket={basket} />
		</header>
	);
}

function BasketIcon({ basket }: { basket: number }) {
	return (
		<span className="relative grid size-8 place-items-center rounded-full bg-(--mk-mist)">
			<ShoppingBagIcon className="size-4" aria-hidden />
			{basket > 0 ? (
				<span
					key={basket}
					className="mk-pop absolute -top-1 -right-1 grid min-w-4 place-items-center rounded-full bg-(--mk-signal) px-1 font-semibold text-(--mk-white) text-[10px] leading-4"
				>
					{basket}
				</span>
			) : null}
		</span>
	);
}

function ProductRow({
	frame,
	products,
}: {
	frame: HeroFrame;
	products: HeroProduct[];
}) {
	return (
		<div className="grid min-h-[146px] grid-cols-3 gap-1.5">
			{products.map((p, i) => (
				<div
					key={`${frame.scene}-${p.id}`}
					className={cn(
						"flex flex-col rounded-2xl bg-(--mk-mist) p-1.5",
						i < frame.cards ? "mk-pop" : "invisible",
					)}
				>
					<img
						src={p.thumb}
						alt=""
						width={88}
						height={88}
						className="aspect-square w-full rounded-xl object-contain"
					/>
					<p className="mt-1 line-clamp-2 min-h-[2.4em] px-0.5 text-[10px] leading-tight">
						{p.title}
					</p>
					<div className="mt-1 flex items-center justify-between px-0.5">
						<span className="font-semibold text-[11px]">
							${p.price}
						</span>
						<span
							className={cn(
								"grid size-5 place-items-center rounded-full transition-colors",
								frame.added
									? "bg-(--mk-signal) text-(--mk-white)"
									: "bg-(--mk-white)",
							)}
						>
							{frame.added ? (
								<CheckIcon className="size-3" aria-hidden />
							) : (
								<PlusIcon className="size-3" aria-hidden />
							)}
						</span>
					</div>
				</div>
			))}
		</div>
	);
}

/** A recipe answer: the dish, each amount matched to the pack that covers it, and Add all. */
function RecipeCard({
	frame,
	products,
	recipe,
}: {
	frame: HeroFrame;
	products: HeroProduct[];
	recipe: NonNullable<Extract<HeroScene, { kind: "chat" }>["recipe"]>;
}) {
	const total = products.reduce((sum, p) => sum + Number(p.price), 0);
	return (
		<div
			className={cn(
				"rounded-2xl bg-(--mk-mist) p-2.5",
				frame.cards > 0 ? "mk-pop" : "invisible",
			)}
		>
			<div className="flex items-center justify-between px-1">
				<p className="flex items-center gap-1.5 font-semibold text-[13px]">
					<ChefHatIcon className="size-3.5" aria-hidden />
					{recipe.dish}
				</p>
				<span className="rounded-full bg-(--mk-white) px-2 py-0.5 text-(--mk-muted) text-[10px]">
					{recipe.serves} servings
				</span>
			</div>
			<ul className="mt-2 space-y-1">
				{products.map((p, i) => (
					<li
						key={`${frame.scene}-${p.id}`}
						className="flex h-10 items-center gap-2 rounded-xl bg-(--mk-white) px-1.5"
					>
						<span className="size-8 shrink-0">
							{i < frame.cards ? (
								<img
									src={p.thumb}
									alt=""
									width={32}
									height={32}
									className="mk-pop size-8 object-contain"
								/>
							) : null}
						</span>
						<span className="w-11 shrink-0 font-semibold text-[11px] tabular-nums">
							{recipe.amounts[i]}
						</span>
						<span className="min-w-0 flex-1 truncate text-[10.5px] text-(--mk-ink-2)">
							{p.title}
						</span>
						<span className="font-semibold text-[11px] tabular-nums">
							${p.price}
						</span>
					</li>
				))}
			</ul>
			<p className="mt-1.5 truncate px-1 text-(--mk-faint) text-[10px]">
				+ {recipe.more}
			</p>
			<span
				className={cn(
					"mt-2 flex h-8 items-center justify-center gap-1.5 rounded-xl font-medium text-[12px] text-(--mk-white) transition-colors duration-(--duration-modal)",
					frame.added ? "bg-(--mk-signal)" : "bg-(--mk-ink)",
				)}
			>
				{frame.added ? (
					<>
						<CheckIcon className="size-3.5" aria-hidden />
						Added to basket
					</>
				) : (
					`Add all · $${total.toFixed(2)}`
				)}
			</span>
		</div>
	);
}

function BasketBar({ frame }: { frame: HeroFrame }) {
	return (
		<div
			className={cn(
				"mx-3.5 mb-2 flex items-center justify-between rounded-2xl bg-(--mk-ink) px-3.5 py-2.5 text-(--mk-white) transition-[opacity,transform] duration-(--duration-modal) ease-(--ease-out-ui)",
				frame.basket > 0
					? "translate-y-0 opacity-100"
					: "translate-y-2 opacity-0",
			)}
		>
			<span className="text-[12px]">
				{frame.basket} items ·{" "}
				<span className="font-semibold">${frame.total.toFixed(2)}</span>
			</span>
			<span className="font-medium text-[12px]">View basket</span>
		</div>
	);
}

const VOICE_BARS = [0.5, 0.9, 0.6, 1, 0.7, 0.45, 0.85].map((height, i) => ({
	id: `bar-${i}`,
	height,
}));

const CALL_BUTTONS = [
	{ id: "mute", Icon: MicOffIcon },
	{ id: "keypad", Icon: GripIcon },
	{ id: "speaker", Icon: Volume2Icon },
];

/** A call with the store: who is on the line, what is said (as captions), what goes in the basket. */
function CallView({
	frame,
	products,
}: {
	frame: HeroFrame;
	products: HeroProduct[];
}) {
	const minutes = Math.floor(frame.elapsed / 60);
	const seconds = String(frame.elapsed % 60).padStart(2, "0");
	const agentSpeaking = frame.speaking === "agent";
	return (
		<>
			<div className="flex flex-col items-center px-4 pt-4 text-center">
				<span className="relative grid size-16 place-items-center">
					{agentSpeaking ? (
						<span className="mk-pulse absolute inset-0 rounded-[20px] bg-(--mk-signal)" />
					) : null}
					<LogoMark className="relative size-16" />
				</span>
				<p className="mt-3 font-semibold text-[15px] leading-tight">
					{DEMO_STORE.name}
				</p>
				<p className="mt-0.5 text-(--mk-muted) text-[12px] tabular-nums">
					Voice assistant · {minutes}:{seconds}
				</p>
				<span
					aria-hidden
					className={cn(
						"mt-3 flex h-5 items-center gap-[3px]",
						frame.speaking ? "mk-bars" : "opacity-30",
					)}
				>
					{VOICE_BARS.map((bar, i) => (
						<span
							key={bar.id}
							className={cn(
								"block w-[3px] rounded-full",
								agentSpeaking
									? "bg-(--mk-signal)"
									: "bg-(--mk-ink)",
							)}
							style={{
								height: `${bar.height * 100}%`,
								animationDelay: `${i * -0.13}s`,
							}}
						/>
					))}
				</span>
			</div>

			<div className="mk-noscroll flex flex-1 flex-col gap-2 overflow-hidden px-4 pt-5 pb-3">
				{frame.spoken.slice(-3).map((line, i, shown) => (
					<p
						// Its place in the whole call: stable while later lines arrive.
						key={`line-${frame.spoken.length - shown.length + i}`}
						className={cn(
							"text-[13px] leading-snug",
							line.who === "caller"
								? "ms-auto max-w-[85%] text-end text-(--mk-muted)"
								: "max-w-[92%] text-(--mk-ink)",
							i < shown.length - 1 && "text-(--mk-muted)",
						)}
					>
						<span className="block font-medium text-(--mk-faint) text-[10px]">
							{line.who === "caller" ? "Shopper" : "Assistant"}
						</span>
						<span dir="auto" className="block text-[14px]">
							{line.text}
						</span>
						{line.en && line.done ? (
							<span className="mk-pop mt-0.5 block text-(--mk-faint) text-[11px]">
								{line.en}
							</span>
						) : null}
					</p>
				))}
				<div className="mt-auto">
					<ProductRow frame={frame} products={products} />
				</div>
			</div>
			<BasketBar frame={frame} />
			<div className="flex items-center justify-center gap-4 px-6 pt-1 pb-5">
				{CALL_BUTTONS.map((button) => (
					<span
						key={button.id}
						className="grid size-11 place-items-center rounded-full bg-(--mk-mist)"
					>
						<button.Icon className="size-[18px]" aria-hidden />
					</span>
				))}
				<span className="grid size-11 place-items-center rounded-full bg-(--mk-stop) text-(--mk-white)">
					<PhoneOffIcon className="size-[18px]" aria-hidden />
				</span>
			</div>
		</>
	);
}
